// Tiny line-level diff helper for the suggestion review UI. Returns an
// aligned pair of arrays — one entry per "row" with kind = same | removed
// | added. Good enough for a side-by-side prompt diff; not trying to
// replace `diff-match-patch`.
//
// Algorithm: standard LCS over normalized lines. Lines are normalized by
// trimming trailing whitespace; leading whitespace is preserved so prompt
// indentation diffs cleanly. Empty input → single same-row pair of `""`.

export type DiffRowKind = "same" | "removed" | "added";

export type DiffRow = {
  kind: DiffRowKind;
  /** Base-side line (null when this row is purely additive). */
  base: string | null;
  /** Suggested-side line (null when this row is purely a removal). */
  suggested: string | null;
};

export function diffLines(base: string, suggested: string): DiffRow[] {
  const a = splitLines(base);
  const b = splitLines(suggested);
  if (a.length === 0 && b.length === 0) return [{ kind: "same", base: "", suggested: "" }];

  const lcs = buildLcsTable(a, b);
  const rows: DiffRow[] = [];
  let i = a.length;
  let j = b.length;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      rows.push({ kind: "same", base: a[i - 1], suggested: b[j - 1] });
      i -= 1;
      j -= 1;
    } else if (lcs[i - 1][j] >= lcs[i][j - 1]) {
      rows.push({ kind: "removed", base: a[i - 1], suggested: null });
      i -= 1;
    } else {
      rows.push({ kind: "added", base: null, suggested: b[j - 1] });
      j -= 1;
    }
  }
  while (i > 0) {
    rows.push({ kind: "removed", base: a[i - 1], suggested: null });
    i -= 1;
  }
  while (j > 0) {
    rows.push({ kind: "added", base: null, suggested: b[j - 1] });
    j -= 1;
  }
  return rows.reverse();
}

function splitLines(input: string): string[] {
  return String(input).split(/\r?\n/).map((line) => line.replace(/\s+$/, ""));
}

function buildLcsTable(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const table: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      if (a[i - 1] === b[j - 1]) table[i][j] = table[i - 1][j - 1] + 1;
      else table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
    }
  }
  return table;
}

/** Quick "is this changed at all?" check without computing the full diff. */
export function isUnchanged(base: string, suggested: string): boolean {
  return base === suggested;
}
